import {CustomRequestOption, isFile, Segment, Upload, UploadState} from 'platform/components';
import {VStack} from 'platform/foundation';

import {useUploadFileMutation} from '@dms/api/base';
import {CompositionImageLayer} from '@dms/api/shared';
import i18n from '@dms/i18n';
import {handleApiError} from '@dms/shared';

import {ApiError, suffixTestId, TestIdProps} from 'shared/web';

import {notifyUnsupportedImageFormat} from '../../../utils/notifyUnsupportedImageFormat';
import {IMAGE_LAYER_ACCEPT} from '../constants/imageLayerAccept';

interface ImageLayerFieldsProps extends TestIdProps {
  layer: CompositionImageLayer;
  onChange: (patch: Partial<CompositionImageLayer>) => void;
  onImageUploaded: (fileId: string, url: string) => void;
}

const FIT_OPTIONS: {value: CompositionImageLayer['fit']; label: string}[] = [
  {value: 'contain', label: i18n.t('entity.watermark.composition.labels.fitContain')},
  {value: 'cover', label: i18n.t('entity.watermark.composition.labels.fitCover')},
  {value: 'fill', label: i18n.t('entity.watermark.composition.labels.fitFill')},
];

export function ImageLayerFields(props: ImageLayerFieldsProps) {
  const [uploadFile, {isLoading: isUploading}] = useUploadFileMutation();

  const handleReplace = (option: CustomRequestOption) => {
    if (!isFile(option.file)) {
      return;
    }
    uploadFile({file: option.file})
      .unwrap()
      .then((uploaded) => {
        props.onImageUploaded(uploaded.fileId, uploaded.fileUri);
        props.onChange({fileId: uploaded.fileId});
        option.onSuccess?.({});
      })
      .catch((error: ApiError) => {
        handleApiError(error);
        option.onError?.(new Error('Image upload failed'));
      });
  };

  return (
    <VStack spacing={3}>
      <Upload
        type="button"
        accept={IMAGE_LAYER_ACCEPT}
        shouldValidateAccept
        buttonVariant="secondary"
        uploadIcon="image/image"
        uploadText={i18n.t('entity.watermark.composition.actions.replaceImage')}
        uploadingText={i18n.t('general.labels.uploadingWithDots')}
        uploadState={isUploading ? UploadState.Uploading : UploadState.Idle}
        customRequest={handleReplace}
        onError={notifyUnsupportedImageFormat}
        data-testid={suffixTestId('replaceImage', props)}
      />
      <Segment<CompositionImageLayer['fit']>
        label={i18n.t('entity.watermark.composition.labels.fit')}
        value={props.layer.fit}
        options={FIT_OPTIONS}
        onChange={(fit) => props.onChange({fit})}
        data-testid={suffixTestId('fit', props)}
      />
    </VStack>
  );
}
